import React, { useMemo, useRef, useState } from "react";
import {
  ArrowContainer,
  Container,
  LeftContent,
  RightContent,
  RightContentOverlay,
  StyledVideo,
  StyledImage,
  UploadVideoContainer,
  UploadVideoButton,
  DeleteButton,
  GenerateContainer,
  IconWrapper,
  TooltipContainer,
  TooltipText,
  GenerateButton,
  InfoIconWrapper,
  ContainerWrapper,
  GenerateContainerMobile,
  UploadVideoTitle,
  UploadVideoDesc,
} from "./ExtendVideo.styles";
import { ReactComponent as ArrowIcon } from "../../assets/icons/arrow.svg";
import { ReactComponent as SettingsIcon } from "../../assets/icons/settings.svg";
import { ReactComponent as InfoIcon } from "../../assets/icons/info-circle.svg";
import { ReactComponent as UploadCloudIcon } from "../../assets/icons/upload-cloud.svg";
import {
  selectGenerateCollection,
  selectRegenerateCollectionPending,
  selectSelectedCollection,
} from "../../store/slices/model/slice";
import { useDispatch, useSelector } from "react-redux";
import {
  DEFAULT_VIDEO_DURATION,
  DEFAULT_VIDEO_FPS,
  DEFAULT_VIDEO_RESOLUTION,
  MAX_VIDEO_SIZE,
  MEDIA_TYPE,
  NUDIFY_MODEL_NAME,
  REGENERATE_DISCOUNT_PERCENTAGE,
  VIDEO_DURATION_PRICE,
  VIDEO_FPS_PRICE,
  VIDEO_RESOLUTION_PRICE,
} from "../../utils/constants";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  generateCollection,
  regenerateCollection,
} from "../../store/slices/model/asyncThunks";
import { ReactComponent as DeleteIcon } from "../../assets/icons/delete.svg";
import EnterPromptModal from "../EnterPromptModal/EnterPromptModal";
import GradientStarIcon from "../GradientStarIcon/GradientStarIcon";
import { useMediaQuery } from "react-responsive";

const ExtendVideo = ({ onClose }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const fileInputRef = useRef();
  const collection = useSelector(selectSelectedCollection);
  const { loading: extendLoading } = useSelector(selectGenerateCollection);
  const regenerateLoading = useSelector(selectRegenerateCollectionPending);
  const [uploadedVideo, setUploadedVideo] = useState(null);
  const [isEnterPromptModalOpen, setIsEnterPromptModalOpen] = useState(false);
  const [customPrompt, setCustomPrompt] = useState("");
  const [customSeed, setCustomSeed] = useState(
    collection?.seed || Math.floor(Math.random() * 10000000) + 1
  );
  const isMobile = useMediaQuery({ maxWidth: 768 });

  const [videoResolution, setVideoResolution] = useState(
    collection?.regenerate
      ? collection?.output?.resolution ?? DEFAULT_VIDEO_RESOLUTION
      : localStorage.getItem("videoResolution") ?? DEFAULT_VIDEO_RESOLUTION
  );

  const [fps, setFps] = useState(
    collection?.regenerate
      ? collection?.output.fps ?? DEFAULT_VIDEO_FPS
      : Number(localStorage.getItem("videoFps") ?? DEFAULT_VIDEO_FPS)
  );

  const [videoDuration, setVideoDuration] = useState(
    collection?.regenerate
      ? collection?.output.videoDuration ?? DEFAULT_VIDEO_DURATION
      : Number(localStorage.getItem("videoDuration") ?? DEFAULT_VIDEO_DURATION)
  );

  const [editedVideoFps, setEditedVideoFps] = useState(false);

  const [showTooltip, setShowTooltip] = useState(false);
  const [editedSetup, setEditedSetup] = useState(false);
  const [hideRegenerateInput, setHideRegenerateInput] = useState(false);
  const [editedResolution, setEditedResolution] = useState(false);
  const [editedVideoDuration, setEditedVideoDuration] = useState(false);

  const isExtendVideoRegenerate = useMemo(
    () => collection?.regenerate && !!collection?.input?.video?.url,
    [collection]
  );

  const collectionPrice = useMemo(() => {
    if (
      !editedSetup &&
      isExtendVideoRegenerate &&
      !hideRegenerateInput &&
      !editedResolution &&
      !editedVideoFps &&
      !editedVideoDuration
    ) {
      return (
        (collection?.price +
          VIDEO_RESOLUTION_PRICE[collection.output.resolution] +
          (VIDEO_DURATION_PRICE[collection.output.videoDuration] ?? 0) +
          (VIDEO_FPS_PRICE[collection.output.fps] ?? 0)) *
        (1 - REGENERATE_DISCOUNT_PERCENTAGE)
      );
    } else {
      return (
        collection?.price +
        VIDEO_RESOLUTION_PRICE[videoResolution] +
        VIDEO_DURATION_PRICE[videoDuration] +
        VIDEO_FPS_PRICE[fps]
      );
    }
  }, [
    editedSetup,
    isExtendVideoRegenerate,
    hideRegenerateInput,
    editedResolution,
    editedVideoFps,
    editedVideoDuration,
    collection?.price,
    collection.output.resolution,
    collection.output.videoDuration,
    collection.output.fps,
    videoResolution,
    videoDuration,
    fps,
  ]);

  if (!collection) {
    return null;
  }

  const output = collection.output;
  const input = collection.input;

  const handleFileChange = (e) => {
    const { files } = e.target;

    const file = files[0];

    if (
      file &&
      (file.type === "video/mp4" || file.type.startsWith("video/quicktime"))
    ) {
      if (file.size > MAX_VIDEO_SIZE) {
        toast.error("Video must be smaller than 20MB");
        return;
      }
      setUploadedVideo({ file, url: URL.createObjectURL(file) });
      if (isExtendVideoRegenerate) {
        setEditedSetup(true);
      }
    } else {
      toast.error("Unsupported media type.");
    }
  };

  const onExtendClick = () => {
    if ((localStorage.getItem("wasPromptModalOpen") ?? false) === false) {
      setIsEnterPromptModalOpen(true);
      localStorage.setItem("wasPromptModalOpen", true);
      return;
    }

    if (
      !editedSetup &&
      isExtendVideoRegenerate &&
      !hideRegenerateInput &&
      !editedResolution &&
      !editedVideoFps &&
      !editedVideoDuration
    ) {
      dispatch(
        regenerateCollection({
          collectionId: collection.collectionId,
          seed: customSeed,
        })
      )
        .unwrap()
        .then(() => {
          toast.success("Regeneration started...");
          navigate("/collection");
          setTimeout(() => {
            window.scrollTo(0, 0);
          }, 300);
          setUploadedVideo(null);
          setCustomPrompt("");
          setIsEnterPromptModalOpen(false);
          onClose();
        })
        .catch((err) => {
          toast.error(err);
        });
    } else {
      dispatch(
        generateCollection({
          video: uploadedVideo,
          customPrompt,
          modelId: collection.model,
          resolution: videoResolution,
          seed: customSeed,
          fps,
          videoDuration,
        })
      )
        .unwrap()
        .then(() => {
          toast.success("Extending started...");

          const alreadyGeneratedVideo = localStorage.getItem(
            "alreadyGeneratedVideo"
          );

          if (!alreadyGeneratedVideo) {
            navigate("/collection");
            setTimeout(() => {
              window.scrollTo(0, 0);
            }, 300);
          }

          setUploadedVideo(null);
          setCustomPrompt("");
          setIsEnterPromptModalOpen(false);
          onClose();
        })
        .catch((err) => {
          toast.error(err);
        });
    }
  };

  const renderGenerateContainer = () => {
    return (
      <>
        <IconWrapper
          onClick={() => {
            setIsEnterPromptModalOpen(true);
            localStorage.setItem("wasPromptModalOpen", true);
          }}
        >
          <SettingsIcon />
        </IconWrapper>
        <GenerateButton
          disabled={extendLoading || regenerateLoading}
          onClick={onExtendClick}
        >
          {extendLoading || regenerateLoading ? (
            <l-line-spinner size="16" stroke="2" speed="1" color="#000" />
          ) : (
            <>
              {!editedSetup &&
              isExtendVideoRegenerate &&
              !hideRegenerateInput &&
              !editedResolution &&
              !editedVideoFps &&
              !editedVideoDuration
                ? "Regenerate"
                : "Extend"}
              <GradientStarIcon />
              {Math.ceil(collectionPrice)}
            </>
          )}
        </GenerateButton>
        <InfoIconWrapper
          onMouseEnter={() => {
            if (!isMobile) {
              setShowTooltip(true);
            }
          }}
          onMouseLeave={() => {
            if (!isMobile) {
              setShowTooltip(false);
            }
          }}
          onClick={() => {
            if (isMobile) {
              setShowTooltip(!showTooltip);
            }
          }}
        >
          <InfoIcon width={24} height={24} />
          {showTooltip && (
            <TooltipContainer>
              <TooltipText>
                Extending a video reuses its last frame as the starting point
                for the new generation, making the final video longer and
                seamless.
              </TooltipText>
            </TooltipContainer>
          )}
        </InfoIconWrapper>
      </>
    );
  };

  return (
    <ContainerWrapper>
      <Container>
        <LeftContent>
          <UploadVideoContainer>
            {uploadedVideo && (
              <DeleteButton
                onClick={() => {
                  setUploadedVideo(null);
                  if (isExtendVideoRegenerate) {
                    setEditedSetup(false);
                  }
                }}
              >
                <DeleteIcon />
              </DeleteButton>
            )}
            {uploadedVideo ? (
              <StyledVideo src={uploadedVideo?.url} />
            ) : isExtendVideoRegenerate &&
              !editedSetup &&
              !hideRegenerateInput ? (
              <>
                <DeleteButton
                  onClick={() => {
                    setHideRegenerateInput(true);
                    setEditedSetup(true);
                  }}
                >
                  <DeleteIcon />
                </DeleteButton>
                <StyledImage
                  src={input.image.resizedImage.url || input.image.url}
                />
              </>
            ) : (
              <UploadVideoButton onClick={() => fileInputRef.current?.click()}>
                <UploadCloudIcon />
                <UploadVideoTitle>Upload Video</UploadVideoTitle>
                <UploadVideoDesc>
                  The last frame of the video you upload will be used as the
                  input.
                </UploadVideoDesc>
              </UploadVideoButton>
            )}

            <input
              type="file"
              ref={fileInputRef}
              accept=".mp4,.mov"
              onChange={handleFileChange}
              style={{ display: "none" }}
            />
          </UploadVideoContainer>
          <GenerateContainer>{renderGenerateContainer()}</GenerateContainer>
        </LeftContent>
        <RightContent>
          {output.type === MEDIA_TYPE.VIDEO ? (
            <StyledVideo
              src={output.video.compressedUrl || output.video.url}
              imageSrc={input.image.resizedImage.url || input.image.url}
            />
          ) : (
            <StyledImage src={output.image.url} />
          )}
          <RightContentOverlay />
          {/* <ModelName>{formatModelName(collection.name)}</ModelName>
          <Username>@{collection.username}</Username> */}
        </RightContent>
        <ArrowContainer>
          <ArrowIcon />
        </ArrowContainer>
        <EnterPromptModal
          isOpen={isEnterPromptModalOpen}
          isRegenerate={!!collection}
          onClose={() => setIsEnterPromptModalOpen(false)}
          customPrompt={customPrompt}
          setCustomPrompt={setCustomPrompt}
          videoResolution={videoResolution}
          setVideoResolution={setVideoResolution}
          setEditedResolution={setEditedResolution}
          collectionVideoResolution={collection.output.resolution}
          enableVideoQuality={collection.modelName !== NUDIFY_MODEL_NAME}
          modelPrice={collection.price}
          regeneratePrice={
            (collection?.price +
              VIDEO_RESOLUTION_PRICE[collection.output.resolution] +
              (VIDEO_DURATION_PRICE[collection.output.videoDuration] ?? 0) +
              (VIDEO_FPS_PRICE[collection.output.fps] ?? 0)) *
            (1 - REGENERATE_DISCOUNT_PERCENTAGE)
          }
          isEdited={editedSetup || !isExtendVideoRegenerate}
          setCustomSeed={setCustomSeed}
          customSeed={customSeed}
          collectionSeed={collection?.seed}
          fps={fps}
          setFps={setFps}
          setEditedVideoFps={setEditedVideoFps}
          collectionVideoFps={collection.output.fps}
          videoDuration={videoDuration}
          setVideoDuration={setVideoDuration}
          collectionVideoDuration={collection.output.videoDuration}
          setEditedVideoDuration={setEditedVideoDuration}
        />
      </Container>
      <GenerateContainerMobile>
        {renderGenerateContainer()}
      </GenerateContainerMobile>
    </ContainerWrapper>
  );
};

export default ExtendVideo;
